import React, { type FC } from 'react';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';

import { Flex, FlexColumn } from 'src/components/flex';
import SearchInput from 'src/pages/landlord/conversations/components/header/ConversationsSearchInput';
import FilterStatus from 'src/pages/landlord/conversations/components/header/ConversationsFilterStatus';
import FilterButton from 'src/pages/landlord/conversations/components/header/ConversationsFilterButton';
import InboxFilterGroup from 'src/pages/landlord/conversations/components/header/ConversationsInboxFilterGroup';
import type { TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import useCurrentInbox from 'src/services/api/inboxes/use-current-inbox';
import type { IInbox } from 'src/services/api/inboxes/types';
import type { IOtherUser, IUser } from 'src/api/users-api';
import { useProperties } from 'src/services/api';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import GroupBySelector, { type GroupByOption } from 'src/pages/landlord/conversations/components/header/GroupBySelector';

type ConversationsHeaderProps = {
    searchScope: 'inbox' | 'all';
    filter: TConversationsFilter;
    setFilter: (newFilter: Partial<TConversationsFilter>) => void;
    resetFilter: () => void;
    search: string;
    setSearch: (newSearch: string) => void;
    isFilterOpen: boolean;
    setFilterOpen: (open: boolean) => void;
    inbox?: IInbox;
    groupBy?: GroupByOption;
    setGroupBy?: (value: GroupByOption) => void;
}

type ConversationsInboxHeaderProps = {
    inbox?: IInbox;
    teamMembers: Array<IUser | IOtherUser>;
}

const ConversationsInboxHeader = ({ inbox, teamMembers }: ConversationsInboxHeaderProps) => {
    return (
        <Flex alignItems="center" flexWrap="wrap" gap={2} sx={{ width: '100%', minWidth: 0 }}>
            <Typography variant="h2" fontSize={24} fontWeight={600} lineHeight={1} py={1} className="inbox-title" sx={{ flexShrink: 0 }}>
                {inbox?.name ?? 'Team Inbox'}
            </Typography>
            <InboxFilterGroup teamMembers={teamMembers} inboxFilters={inbox?.filters} />
        </Flex>
    );
};

const SearchHeader = () => {
    return (
        <Typography variant="h2" fontSize={24} fontWeight={600} lineHeight={1} py={1}>
            Search Conversations
        </Typography>
    );
};

const ConversationsHeader: FC<ConversationsHeaderProps> = ({
    searchScope,
    filter,
    setFilter,
    resetFilter,
    search,
    setSearch,
    isFilterOpen,
    setFilterOpen,
    inbox: inboxProp,
    groupBy,
    setGroupBy,
}) => {
    const { pathname } = useLocation();
    const isInboxesPage = pathname.startsWith('/landlord/inner/inboxes');

    const { inbox: currentInbox } = useCurrentInbox();
    const inbox = inboxProp ?? currentInbox;

    // Fetch data using queries
    const { data: properties } = useProperties();
    const { data: teamMembers = [] } = useTeamMembers({ includeInactive: true });
    return (
        <>
            <FlexColumn p={2}>
                {isInboxesPage ? (
                    <ConversationsInboxHeader
                        inbox={inbox}
                        teamMembers={teamMembers}
                    />
                ) : <SearchHeader />}
                <Flex alignItems="center" gap={2}>
                    <SearchInput
                        searchScope={searchScope}
                        search={search}
                        setSearch={setSearch}
                    />
                    <FilterButton
                        isFilterOpen={isFilterOpen}
                        setFilterOpen={setFilterOpen}
                        filter={filter}
                        inboxFilters={isInboxesPage ? inbox?.filters : undefined}
                        properties={properties ?? []}
                        setFilter={setFilter}
                        resetFilter={resetFilter}
                    />
                    {groupBy !== undefined && setGroupBy && (
                        <GroupBySelector value={groupBy} onChange={setGroupBy} />
                    )}
                </Flex>

                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{
                        '& .MuiChip-root': { maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' },
                        '& .MuiSvgIcon-root': { ml: 1, fontSize: 20 }
                    }}
                >
                    <FilterStatus
                        teamMembers={teamMembers}
                        filter={filter}
                        setFilter={setFilter}
                    />
                </Flex>
            </FlexColumn>
        </>
    );
};

export default ConversationsHeader;
